import moment from 'moment'

import { createNotification } from '@/app/module/notifications/store/actions'
import request from '@/app/module/request'
import {
  ACTION_DOWNLOAD_AUDIT_LOG_FAILURE,
  ACTION_DOWNLOAD_AUDIT_LOG_REQUEST,
  ACTION_DOWNLOAD_AUDIT_LOG_SUCCESS,
  ACTION_GET_USER_FAILURE,
  ACTION_GET_USER_REQUEST,
  ACTION_GET_USER_SUCCESS,
  ACTION_SWITCH_ORGANIZATION_FAILURE,
  ACTION_SWITCH_ORGANIZATION_REQUEST,
  ACTION_SWITCH_ORGANIZATION_SUCCESS,
  ACTION_TOGGLE_CALL_DIALOG,
  ERROR_DOWNLOAD_AUDIT_LOG,
  ERROR_GET_USER_SERVER,
  ERROR_SWITCH_ORGANIZATION_SERVER,
} from '@/app/module/user/definitions'
import { createAsyncActions } from '@/app/service/util'
import { downloadFile } from '@/app/service/util/download'
import { isFailed } from '@/app/service/util/error'
import { esApi } from '@/app/store/api'
import http from '@/config/http'

const {
  request: getUserRequest,
  success: getUserSuccess,
  failure: getUserFailure,
} = createAsyncActions({
  request: ACTION_GET_USER_REQUEST,
  success: ACTION_GET_USER_SUCCESS,
  failure: ACTION_GET_USER_FAILURE,
})

const {
  request: switchOrganizationRequest,
  success: switchOrganizationSuccess,
  failure: switchOrganizationFailure,
} = createAsyncActions({
  request: ACTION_SWITCH_ORGANIZATION_REQUEST,
  success: ACTION_SWITCH_ORGANIZATION_SUCCESS,
  failure: ACTION_SWITCH_ORGANIZATION_FAILURE,
})

const {
  request: downloadAuditLogRequest,
  success: downloadAuditLogSuccess,
  failure: downloadAuditLogFailure,
} = createAsyncActions({
  request: ACTION_DOWNLOAD_AUDIT_LOG_REQUEST,
  success: ACTION_DOWNLOAD_AUDIT_LOG_SUCCESS,
  failure: ACTION_DOWNLOAD_AUDIT_LOG_FAILURE,
})

export {
  getUserFailure,
  getUserRequest,
  getUserSuccess,
  switchOrganizationFailure,
  switchOrganizationRequest,
  switchOrganizationSuccess,
}

let pageLoadSource = ''

export const setPageLoadSource = (source) => {
  try {
    if (typeof source === 'string') {
      pageLoadSource = source
    }
  } catch {
    // ignore
  }
}

export const getPageLoadSource = () => {
  try {
    const source = pageLoadSource
    pageLoadSource = ''
    return source
  } catch {
    // ignore
  }

  return ''
}

export const getUser =
  ({ token } = {}) =>
  (dispatch) => {
    dispatch(getUserRequest({ token }))

    if (!token) {
      dispatch(getUserFailure('Missing authentication token.'))
      return Promise.resolve({ token })
    }

    return request.user
      .getDetails({ token })
      .then((user) => {
        if (user.orgs?.length) {
          const org = user.orgs.find((o) => o.orgId === user.activeOrgId)
          if (org && org.permissions.plans) {
            dispatch(esApi.endpoints.getOrgPlan.initiate({ orgId: user.activeOrgId.toString() }))
          }
        }
        const source = getPageLoadSource()
        dispatch(getUserSuccess({ ...user, source }))
        return {
          ...user,
          token,
        }
      })
      .catch((err) => {
        dispatch(
          getUserFailure({
            status: err.status,
            message: ERROR_GET_USER_SERVER,
          }),
        )
        return err
      })
  }

export const switchOrganization =
  ({ token, item = {} }) =>
  (dispatch) => {
    dispatch(switchOrganizationRequest(item))

    return request.user
      .switchOrganization({ token, item: { activeOrgId: item.value } })
      .then(() => {
        setPageLoadSource('switch-organization')
        dispatch(switchOrganizationSuccess(item))
        return item
      })
      .catch((err = {}) => {
        dispatch(
          createNotification({
            'switch-organization': {
              type: 'error',
              message: err.msg || ERROR_SWITCH_ORGANIZATION_SERVER,
            },
          }),
        )
        return err
      })
      .then(() => getUser({ token })(dispatch))
      .then(() => item)
  }

export const downloadAuditLog =
  ({ token, orgId, from, to }) =>
  async (dispatch) => {
    dispatch(downloadAuditLogRequest())
    const filename = http.auditLog.download.getSignablePath({ orgId })
    const data = {
      paths: [filename],
    }

    try {
      const res = await request.auth.sign({ token, item: data })
      if (isFailed(res)) {
        dispatch(
          downloadAuditLogFailure({
            status: res.status,
            message: ERROR_DOWNLOAD_AUDIT_LOG,
          }),
        )
        dispatch(
          createNotification({
            'download-audit-log-failed': {
              type: 'error',
              message: ERROR_DOWNLOAD_AUDIT_LOG,
            },
          }),
        )
        return res
      }

      const url = http.files.get.getPath({
        signedPath: filename,
        urlToken: res[filename],
      })
      const urlWithDates = `${url}&startDate=${from}&endDate=${to}`
      const name = `engageSPARK_auditlog_${moment(from).format('YYYY/MM/DD')}-${moment(to).format('YYYY/MM/DD')}.csv`
      await downloadFile(urlWithDates, name)
      dispatch(downloadAuditLogSuccess())
      return res
    } catch (err) {
      dispatch(
        downloadAuditLogFailure({
          status: err.status,
          message: ERROR_DOWNLOAD_AUDIT_LOG,
        }),
      )
      dispatch(
        createNotification({
          'download-audit-log-failed': {
            type: 'error',
            message: ERROR_DOWNLOAD_AUDIT_LOG,
          },
        }),
      )
      return err
    }
  }

export const toggleCallDialog = (payload) => ({
  type: ACTION_TOGGLE_CALL_DIALOG,
  value: payload,
})
